import type { ThemeType } from "@repo/shared/types/theme";
import { AllCommunityModule, ModuleRegistry } from "ag-charts-community";
import { type AgChartProps, AgCharts } from "ag-charts-react";
import type {
  AgBaseCartesianAxisOptions,
  AgChartOptions,
  AgHighlightStyleOptions,
  AgMultiSeriesHighlightOptions,
  AgSeriesMarkerOptions,
} from "ag-charts-types";
import chroma from "chroma-js";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  graphDark,
  graphLight,
  graphScaleDark,
  graphScaleLight,
} from "../graph-colors";

// Enable all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

function getCSSVariable(variableName: string): string {
  if (typeof document === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
}

/**
 * AG Charts expects comma-separated rgb/rgba. Chroma's .css() uses space-separated (modern CSS).
 * Format chroma instance as "rgb(r, g, b)" or "rgba(r, g, b, a)".
 */
function chromaToRgbCss(c: ReturnType<typeof chroma>): string {
  const [r, g, b, a] = c.rgba();
  if (a < 1) return `rgba(${r}, ${g}, ${b}, ${a})`;
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Converts a CSS color value to rgb for AG Charts.
 * Uses chroma-js (parses hex, rgb, hsl, oklch, etc.); output is comma-separated for AG Charts.
 */
function cssColorToRgb(cssValue: string): string {
  if (!cssValue) return "";
  const trimmed = cssValue.trim();
  if (!chroma.valid(trimmed)) return "";
  return chromaToRgbCss(chroma(trimmed));
}

/** Reads CSS custom properties and converts them to rgb for AG Charts theme params. */
function buildChartThemeParams() {
  const toRgb = (v: string, fallback: string) => {
    const raw = getCSSVariable(v);
    if (!raw) return fallback;
    const rgb = cssColorToRgb(raw);
    return rgb || fallback;
  };
  const muted = toRgb("--muted-foreground", "rgb(115, 115, 115)");
  const foreground = toRgb("--foreground", "rgb(23, 23, 23)");
  const card = toRgb("--card", "rgb(255, 255, 255)");
  // Gridlines render as solid 1px strokes, so they read visually heavier than
  // anti-aliased text at the same RGB. Use --border (lighter than
  // --muted-foreground) so gridlines perceptually match the axis labels.
  const border = toRgb("--border", "rgb(229, 229, 229)");
  return {
    textColor: muted,
    subtleTextColor: muted,
    fontFamily: "'Geist Mono', monospace",
    fontSize: 11,
    chromeBackgroundColor: card,
    foregroundColor: muted,
    chromeSubtleTextColor: muted,
    chromeTextColor: foreground,
    gridLineColor: border,
  };
}

const noDataOverlay = () =>
  [
    "<div",
    '    style="',
    "        align-items: center;",
    "        box-sizing: border-box;",
    "        color: var(--foreground);",
    "        display: flex;",
    "        height: 100%;",
    "        justify-content: center;",
    '    "',
    ">",
    "No data available",
    "</div>",
  ].join("\n");

const highlight: AgMultiSeriesHighlightOptions<
  AgHighlightStyleOptions,
  AgHighlightStyleOptions
> = {
  enabled: true,
  // highlightedSeries: {
  //   fillOpacity: 1,
  //   strokeOpacity: 1,
  //   opacity: 1,
  // },
  unhighlightedSeries: {
    opacity: 0.4,
  },
  highlightedItem: {
    strokeWidth: 0,
  },
  // unhighlightedItem: {
  //   opacity: 0.4,
  // },
};

/** Draws a square with rounded corners for use as a legend marker shape. */
const roundedSquare = ({
  x,
  y,
  path,
  size,
}: {
  x: number;
  y: number;
  path: any;
  size: number;
}) => {
  const r = size / 2;
  const cr = r * 0.3;
  const pi = Math.PI;
  path.clear();
  path.moveTo(x + r - cr, y - r);
  path.arc(x + r - cr, y - r + cr, cr, -pi / 2, 0);
  path.lineTo(x + r, y + r - cr);
  path.arc(x + r - cr, y + r - cr, cr, 0, pi / 2);
  path.lineTo(x - r + cr, y + r);
  path.arc(x - r + cr, y + r - cr, cr, pi / 2, pi);
  path.lineTo(x - r, y - r + cr);
  path.arc(x - r + cr, y - r + cr, cr, pi, -pi / 2);
  path.closePath();
};

/** Diameter of an always-visible marker dot for single-point line/area series. */
const SINGLE_POINT_MARKER_SIZE = 6;
/** Diameter of a marker dot when its data point is highlighted (hovered). */
const HIGHLIGHTED_MARKER_SIZE = 7;

/**
 * Counts the rows that contribute a visible point to a series, i.e. rows whose
 * `yKey` value is non-null. Line/area series connect across missing data, so a
 * series can have a single visible point even when the dataset has many rows.
 */
const countSeriesPoints = (
  data: readonly Record<string, unknown>[] | undefined,
  yKey: string | undefined,
): number => {
  if (!Array.isArray(data) || !yKey) return 0;
  let count = 0;
  for (const row of data) {
    if (row?.[yKey] != null) count += 1;
  }
  return count;
};

/**
 * Builds the marker options for a line/area series. Dots are hidden at rest and
 * revealed on hover, except when the series has a single visible point (which
 * would otherwise render no line segment at all) — then the dot stays visible.
 */
const buildLineAreaMarker = (
  color: string,
  alwaysVisible: boolean,
): AgSeriesMarkerOptions<any, any> => {
  const fill = chromaToRgbCss(chroma(color).alpha(1));
  const restSize = alwaysVisible ? SINGLE_POINT_MARKER_SIZE : 0;
  return {
    enabled: true,
    shape: "circle",
    fill,
    size: restSize,
    itemStyler: (params: any) => {
      if (params.highlightState === "highlighted-item") {
        return {
          size: HIGHLIGHTED_MARKER_SIZE,
          fill,
          fillOpacity: 1,
          strokeOpacity: 0,
        };
      }
      return { size: restSize };
    },
  };
};

const baseAxesConfig = {
  gridLine: {
    enabled: false,
  },
  line: {
    enabled: false,
  },
  label: {
    enabled: true,
    fontFamily: "'Geist Mono', monospace",
    fontSize: 11,
    wrapping: "never",
    // truncate: true,
    autoRotate: false,
  },
  title: {
    // Spacing between the axis labels/ticks and the axis title
    spacing: 16,
    fontSize: 11,
  },
} satisfies Omit<AgBaseCartesianAxisOptions, "type">;

const chartContainerClasses =
  "w-full h-full bg-background select-text overflow-hidden";

/** Width below which the chart switches to compact mode (no legend, axes labels, etc.) */
const SMALL_CHART_WIDTH = 160;
/** Height below which the chart switches to compact mode */
const SMALL_CHART_HEIGHT = 160;

const COMPACT_PADDING = { top: 8, bottom: 8, left: 8, right: 8 };

/**
 * Returns a new chart config with compact-mode overrides applied. Does not mutate
 * the input; reuses config.data by reference to avoid copying large datasets.
 * Use this when isCompact so that reverting to large size uses the original config.
 */
function buildCompactConfig(config: AgChartOptions): AgChartOptions {
  const theme = config.theme;
  let themeOverrides: AgChartOptions["theme"] = config.theme;

  if (
    theme &&
    typeof theme === "object" &&
    "overrides" in theme &&
    theme.overrides?.common
  ) {
    const common = theme.overrides.common as Record<string, unknown>;
    const axesOverrides = common.axes as
      | Record<string, Record<string, unknown>>
      | undefined;
    const axesCopy: Record<string, Record<string, unknown>> = {};
    if (axesOverrides && typeof axesOverrides === "object") {
      for (const key of Object.keys(axesOverrides)) {
        const axisConfig = axesOverrides[key];
        if (!axisConfig) continue;
        const label =
          axisConfig.label && typeof axisConfig.label === "object"
            ? {
                ...(axisConfig.label as Record<string, unknown>),
                enabled: false,
              }
            : { enabled: false };
        const { title: _t, ...rest } = axisConfig;
        axesCopy[key] = { ...rest, label };
      }
    }
    themeOverrides = {
      ...theme,
      overrides: {
        ...theme.overrides,
        common: {
          ...common,
          padding: COMPACT_PADDING,
          legend:
            common.legend && typeof common.legend === "object"
              ? {
                  ...(common.legend as Record<string, unknown>),
                  enabled: false,
                }
              : { enabled: false },
          axes: axesCopy,
        },
      },
    };
  }

  const compactLegend =
    "legend" in config && config.legend && typeof config.legend === "object"
      ? { ...(config.legend as Record<string, unknown>), enabled: false }
      : undefined;

  const compactAxes = (() => {
    if (!("axes" in config) || !config.axes) return undefined;
    if (Array.isArray(config.axes)) {
      return (config.axes as Record<string, unknown>[]).map((axis) => {
        const { title: _t, ...rest } = axis;
        const label =
          rest.label && typeof rest.label === "object"
            ? { ...(rest.label as Record<string, unknown>), enabled: false }
            : { enabled: false };
        return { ...rest, label };
      });
    }
    if (typeof config.axes === "object") {
      const result: Record<string, Record<string, unknown>> = {};
      for (const [key, axis] of Object.entries(
        config.axes as Record<string, Record<string, unknown>>,
      )) {
        if (!axis || typeof axis !== "object") continue;
        const { title: _t, ...rest } = axis;
        const label =
          rest.label && typeof rest.label === "object"
            ? { ...(rest.label as Record<string, unknown>), enabled: false }
            : { enabled: false };
        result[key] = { ...rest, label };
      }
      return result;
    }
    return undefined;
  })();

  const compactSeries =
    config.series && Array.isArray(config.series)
      ? (config.series as unknown as Record<string, unknown>[]).map(
          (series) => {
            const out = { ...series };
            if (series.label && typeof series.label === "object") {
              out.label = {
                ...(series.label as Record<string, unknown>),
                enabled: false,
              };
            }
            if (series.sectorLabel && typeof series.sectorLabel === "object") {
              out.sectorLabel = {
                ...(series.sectorLabel as Record<string, unknown>),
                enabled: false,
              };
            }
            if (
              series.calloutLabel &&
              typeof series.calloutLabel === "object"
            ) {
              out.calloutLabel = {
                ...(series.calloutLabel as Record<string, unknown>),
                enabled: false,
              };
            }
            return out;
          },
        )
      : undefined;

  return {
    ...config,
    data: config.data,
    theme: themeOverrides,
    ...(compactLegend !== undefined && { legend: compactLegend }),
    ...(compactAxes !== undefined && { axes: compactAxes }),
    ...(compactSeries !== undefined && { series: compactSeries }),
  } as AgChartOptions;
}

/**
 * Builds the full AG Charts config from the caller `options` plus resolved
 * theme params. Pure: never mutates `options` or its `series` objects (returns
 * fresh series), and keeps `data` by reference so large datasets are not copied.
 * Kept module-level and memoized by the component so unrelated re-renders (e.g.
 * ResizeObserver dimension ticks) do not produce a new options object — a new
 * reference forces `ag-charts-react` into a full `performUpdate` + reflow.
 */
function buildAgConfig({
  options,
  chartThemeParams,
  isLight,
  seriesDataKey,
}: {
  options: AgChartOptions;
  chartThemeParams: ReturnType<typeof buildChartThemeParams>;
  isLight: boolean;
  seriesDataKey?: string;
}): AgChartOptions {
  const config: AgChartOptions = {
    ...options,
    background: {
      visible: false,
    },
    theme: {
      palette: {
        fills: isLight ? graphScaleLight : graphScaleDark,
        strokes: isLight ? graphScaleLight : graphScaleDark,
      },
      params: chartThemeParams,
      overrides: {
        common: {
          title: {
            enabled: false,
          },
          seriesArea: {
            padding: {
              left: 24,
              right: 24,
              top: 16,
              bottom: 8,
            },
          },
          padding: {
            top: 8,
            bottom: 8,
            right: 8,
            left: 8,
          },
          axes: (() => {
            const mutedStroke = chartThemeParams.foregroundColor;
            const gridStroke = chartThemeParams.gridLineColor;
            const axesConfig = {
              ...baseAxesConfig,
              gridLine: { ...baseAxesConfig.gridLine, stroke: gridStroke },
              line: { ...baseAxesConfig.line, stroke: mutedStroke },
              tick: { stroke: mutedStroke },
            };
            return {
              "grouped-category": axesConfig,
              "unit-time": axesConfig,
              log: axesConfig,
              number: axesConfig,
              category: axesConfig,
              // "angle-category": axesConfig,
              // "angle-number": axesConfig,
              "ordinal-time": axesConfig,
              time: axesConfig,
            };
          })(),
          tooltip: {
            enabled: true,
            showArrow: false,
            pagination: true,
            mode: "shared",
            range: "nearest",
            position: {
              anchorTo: "node",
              placement: ["left", "right"],
            },
          },
          legend: {
            enabled: true,
            position: "top",
            toggleSeries: true,
            item: {
              label: {
                fontFamily: "'Geist Mono', monospace",
              },
              showSeriesStroke: true,
              // Removed on Xavi's request
              // maxWidth: 130,
              paddingX: 12,
              paddingY: 12,
              marker: {
                size: 8,
                padding: 4,
              },
            },
            preventHidingAll: true,
            pagination: {},
          },
        },
        line: {
          series: {
            strokeWidth: 2,
            highlight,
          },
        },
        area: {
          series: {
            strokeWidth: 2,
            highlight,
          },
        },
        bar: {
          series: {
            crisp: true,
            cornerRadius: 3,
            strokeWidth: 0,
            highlight,
          },
          legend: {
            item: {
              marker: {
                shape: roundedSquare,
              },
            },
          },
        },
        scatter: {
          series: {
            strokeWidth: 0,
            highlight,
          },
        },
        bubble: {
          series: {
            strokeWidth: 0,
          },
        },
        pie: {
          series: {
            sectorSpacing: 4,
            cornerRadius: 4,
            highlight,
          },
          legend: {
            item: {
              marker: {
                shape: roundedSquare,
              },
            },
          },
        },
        donut: {
          series: {
            sectorSpacing: 4,
            cornerRadius: 4,
            highlight,
          },
          legend: {
            item: {
              marker: {
                shape: roundedSquare,
              },
            },
          },
        },
      },
    },
    overlays: {
      noData: {
        renderer: noDataOverlay,
      },
    },
  };

  // Process axes to ensure unit values have UTC enabled
  const applyUtcToAxis = (axis: any) => {
    if (axis && "unit" in axis) {
      if (typeof axis.unit === "string") {
        return { ...axis, unit: { unit: axis.unit, utc: true } };
      }
      if (typeof axis.unit === "object" && axis.unit !== null) {
        return { ...axis, unit: { ...axis.unit, utc: true } };
      }
    }
    return axis;
  };
  if ("axes" in config && config.axes) {
    if (Array.isArray(config.axes)) {
      (config as any).axes = config.axes.map(applyUtcToAxis);
    } else if (typeof config.axes === "object") {
      const axesCopy: Record<string, any> = {};
      for (const [key, axis] of Object.entries(config.axes)) {
        axesCopy[key] = applyUtcToAxis(axis);
      }
      (config as any).axes = axesCopy;
    }
  }

  // Split scatter/bubble series by root-level categoryKey into multiple series with per-series data
  let seriesData: any = {};
  let titles: string[] = [];
  if (
    seriesDataKey &&
    "series" in config &&
    // Only do this for scatter/bubble series
    config.series?.find((s) => s.type === "scatter" || s.type === "bubble")
  ) {
    seriesData = Object.groupBy(
      config.data as any,
      (row: any) => row[seriesDataKey],
    );
    titles = Object.keys(seriesData);

    config.series = titles.map((title) => ({
      ...(config.series as any)[0],
      title,
      data: seriesData[title],
    }));
  }

  // Apply per-series tweaks. Each series is copied first so this pass never
  // mutates the caller's `options.series` objects (which would corrupt the
  // memoized inputs and leak styling across renders).
  if (config.series && Array.isArray(config.series)) {
    const palette = isLight ? graphScaleLight : graphScaleDark;

    config.series = (config.series as unknown as Record<string, unknown>[]).map(
      (original, index) => {
        const series: any = { ...original };
        const fillStr =
          "fill" in series && typeof series.fill === "string"
            ? series.fill
            : undefined;
        const strokeStr =
          "stroke" in series && typeof series.stroke === "string"
            ? series.stroke
            : undefined;
        let rawColor = fillStr || strokeStr || palette[index % palette.length];
        const color = rawColor ?? palette[0] ?? "#000000";

        if (series.type === "scatter" || series.type === "bubble") {
          if (!fillStr && !strokeStr) {
            rawColor = palette[index % palette.length];
          }
          // Keep fill set so the legend marker and tooltip swatch use the
          // resolved series color. Per-dot rendering is fully overridden by
          // the itemStyler below (which applies alpha 0.6).
          series.fill = rawColor;
        }

        if (fillStr) {
          series.stroke = rawColor;
        }

        // Set the marker behavior for area and line series. Single-point series
        // draw no connecting line, so keep their dot visible instead of hiding it
        // until hover.
        if (series.type === "area" || series.type === "line") {
          series.connectMissingData = true;
          const visiblePoints = countSeriesPoints(
            (config as any).data,
            series.yKey,
          );
          series.marker = buildLineAreaMarker(color, visiblePoints <= 1);
        }

        if (series.type === "scatter" || series.type === "bubble") {
          const seriesColor = rawColor ?? palette[0] ?? "#000000";
          series.itemStyler = (params: any) => {
            if (params.highlighted) {
              return {
                fill: chromaToRgbCss(chroma(seriesColor).alpha(1)),
                stroke: chromaToRgbCss(
                  !isLight
                    ? chroma(seriesColor).brighten(2)
                    : chroma(seriesColor).darken(2),
                ),
                strokeOpacity: 0.6,
                strokeWidth: 1,
              };
            }
            return {
              fill: chromaToRgbCss(chroma(seriesColor).alpha(0.6)),
            };
          };
        }

        if (series.type === "donut" || series.type === "pie") {
          const otherColor = isLight
            ? graphLight.otherData
            : graphDark.otherData;
          const sectorKey =
            (typeof series.sectorLabelKey === "string"
              ? series.sectorLabelKey
              : undefined) ?? "sectorLabel";
          series.itemStyler = (params: any) => {
            if (params.datum?.[sectorKey] === "Other") {
              return { fill: otherColor, stroke: otherColor };
            }
            return undefined;
          };
        }

        // Area fill gradient derived from the series color
        if (series.type === "area") {
          const fillAlpha = isLight ? 0.12 : 0.2;
          const gradientTopAlpha = isLight ? 0.35 : 0.7;

          if (series.stacked) {
            series.fill = chromaToRgbCss(chroma(color).alpha(fillAlpha));
          } else {
            series.fill = {
              type: "gradient",
              rotation: 0,
              colorStops: [
                { color: chromaToRgbCss(chroma(color).alpha(0)) },
                {
                  color: chromaToRgbCss(chroma(color).alpha(gradientTopAlpha)),
                },
              ],
            };
          }
        }

        return series;
      },
    ) as AgChartOptions["series"];
  }

  return config;
}

export interface AGChartProps extends AgChartProps {
  seriesDataKey?: string;
  /** When set, overrides auto-detection to force compact mode on or off */
  compact?: boolean;
  /** Active theme — drives palette selection and chrome colors */
  theme?: ThemeType;
}

const AGChart = ({
  options,
  seriesDataKey,
  compact,
  theme: themeProp = "light",
}: AGChartProps) => {
  const isLight = themeProp === "light";

  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Coalesce ResizeObserver-driven dimension updates. `dimensions` gates the
  // initial chart mount and drives the compact-mode threshold, so the first
  // measurement is applied synchronously (the chart appears as soon as it has a
  // size). Subsequent updates during a continuous gesture — a board
  // drag/resize or a sidepane open/close — are debounced into a single trailing
  // update, instead of re-rendering the chart (and potentially flipping compact
  // mode, forcing an AG Charts reflow) on every animation frame.
  const hasMeasuredRef = useRef(false);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const RESIZE_DEBOUNCE_MS = 150;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;

      if (!hasMeasuredRef.current) {
        hasMeasuredRef.current = true;
        setDimensions({ width, height });
        return;
      }

      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        resizeTimeoutRef.current = null;
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          setDimensions({ width, height });
        });
      }, RESIZE_DEBOUNCE_MS);
    });

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const isCompact =
    compact !== undefined
      ? compact
      : dimensions !== null &&
        (dimensions.width < SMALL_CHART_WIDTH ||
          dimensions.height < SMALL_CHART_HEIGHT);

  const [chartThemeParams, setChartThemeParams] = useState(
    buildChartThemeParams,
  );

  useLayoutEffect(() => {
    setChartThemeParams(buildChartThemeParams());
  }, [themeProp]);

  const baseConfig = useMemo(
    () => buildAgConfig({ options, chartThemeParams, isLight, seriesDataKey }),
    [options, chartThemeParams, isLight, seriesDataKey],
  );

  const finalConfig = useMemo(
    () => (isCompact ? buildCompactConfig(baseConfig) : baseConfig),
    [baseConfig, isCompact],
  );

  return (
    <div ref={containerRef} className={chartContainerClasses}>
      {dimensions !== null && (
        <AgCharts
          options={finalConfig}
          style={{ width: "100%", height: "100%" }}
        />
      )}
    </div>
  );
};

export default AGChart;
